<template>
  <div id="app">
    <router-view />
    <auth-sidebar />
    <notebook-sidebar />
    <worker-sidebar />
    <worker-sidebar-new />
    <company-sidebar />
    <jobs-sidebar />
    <cookie-banner />

    <ModalCourses />
    <ModalCourseInfo />
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import InformationNavigation from '@/components/Header/InformationNavigation.vue';
import PagesNavigation from '@/components/Header/PagesNavigation.vue';
import AuthSidebar from '@/components/Authorization/AuthSidebar.vue';
import NotebookSidebar from '@/components/Notebook/NotebookSidebar.vue';
import FooterComponent from '@/components/FooterComponent.vue';
import WorkerSidebar from '@/components/Workers/WorkerSidebar.vue';
import WorkerSidebarNew from '@/components/Workers/WorkerSidebarNew.vue';
import JobsSidebar from '@/components/Home/JobsSidebar.vue';
import CompanySidebar from '@/components/Company/CompanySidebar.vue';
import CookieBanner from '@/components/CookieBanner.vue';
import { AppModule } from '@/store/modules/app';
import { UserModule } from '@/store/modules/user';
import TemplatesMixin from '@/mixins/TemplatesMixin';
import { ThemesModule } from '@/store/modules/themes';
import { groupEnabledChat, groupFontTemplate } from '@/config/configuration';
import ModalCourseInfo from '@/components/Modals/ModalCourseInfo.vue';
import ModalCourses from '@/components/Modals/ModalCourses.vue';
import { NotebookModule } from './store/modules/notebook';

declare const Main: any;

@Component({
  metaInfo(this: any): any {
    return {
      title: this.pageTitle,
    };
  },
  components: {
    ModalCourses,
    ModalCourseInfo,
    InformationNavigation,
    PagesNavigation,
    FooterComponent,
    AuthSidebar,
    NotebookSidebar,
    WorkerSidebar,
    WorkerSidebarNew,
    JobsSidebar,
    CompanySidebar,
    CookieBanner,
  },
})
export default class App extends mixins(TemplatesMixin) {
  get languageSid() {
    return AppModule.languageSid;
  }

  get isRtl() {
    return AppModule.rtl;
  }

  get pageTitle() {
    return AppModule.setting.html_title;
  }

  get accessTokenApplicant() {
    return UserModule.accessTokenApplicant;
  }

  get accessTokenConsultant() {
    return UserModule.accessTokenConsultant;
  }

  get sid() {
    return AppModule.sid;
  }

  @Watch('accessTokenApplicant')
  @Watch('accessTokenConsultant')
  @Watch('sid')
  onTokenChange() {
    this.startChat();
  }

  @Watch('$route', { deep: true })
  handler() {
    document.body.classList.remove('overflow-hidden');
  }

  @Watch('isRtl', { immediate: true })
  handlerRtl(val: string) {
    document.body.setAttribute('dir', val);
  }

  directApply() {
    window.addEventListener('message', (event) => {
      if (event.data.event === 'direct_apply' && event.data.jobId) {
        if (window.dataLayer) {
          window.dataLayer.push(event.data);
        } else {
          console.log('no dataLayer');
        }
      }
    });
  }

  createFontsTemplate() {
    if (this.sid === 'jig') {
      this.jigCreateFontsUrl();
    } else if (groupFontTemplate(this.sid)) {
      this.talentpoolCreateFontsUrl();
    }
  }

  startEvent() {
    (window as any).support_chat = true;
    (window as any).auth_by_token = true;
    Main.prototype.absoluteLinks = false;
    (window as any).main = new Main(this.languageSid);
    (window as any).main.load_module('chat');
  }

  startChat() {
    if (!groupEnabledChat(this.sid)) return;

    (window as any).ChatConfig = {
      sid: this.languageSid,
      useCookieAuth: false,
      primaryColor: this.defaultColor.primary,
    };
    if (this.accessTokenApplicant) {
      (window as any).ChatConfig.accessToken = this.accessTokenApplicant;
    } else if (UserModule.accessTokenConsultant) {
      (window as any).ChatConfig.accessToken = UserModule.accessTokenConsultant;
    }
    (window as any).ChatWidget?.init();
  }

  async created() {
    this.startChat();

    const htmlElement = document.documentElement;
    htmlElement.setAttribute('sid', this.sid);
    if (this.sid === 'jig') {
      htmlElement.setAttribute('theme', this.sid);
    } else {
      htmlElement.setAttribute('theme', 'default');
    }

    this.createIconUrl();
    this.createFontsTemplate();
    await ThemesModule.getThemeColors({ sid: this.sid }).then(() => {
      this.updateColor();
    });
    await AppModule.getTranslation();
    // await Promise.all([loadTranslation]);
    await AppModule.getSettingPortal({ sid: this.languageSid });
    await AppModule.getSetting({ sid: this.languageSid });
    await AppModule.getJobCountPortal({ sid: this.languageSid });

    if (UserModule.accessTokenApplicant) {
      if (!UserModule.userInfoApplicant) {
        await UserModule.GetUserInfoApplicant();
        await NotebookModule.getApplicantNotebookData();
      }
    }
    if (UserModule.accessTokenConsultant) {
      if (!UserModule.userInfoConsultant) {
        await UserModule.GetUserInfoConsultant();
        await NotebookModule.getConsultantNotebookData();
      }
    }
    if (UserModule.accessTokenCompany) {
      if (!UserModule.userInfoCompany) {
        await UserModule.GetUserInfoCompany();
        await NotebookModule.getCompanyNotebookData();
      }
    }

    this.directApply();

    this.startEvent();
  }
}
</script>
